import { ChangeEvent, useEffect, useRef } from 'react';
import { useHookFormMask } from 'use-mask-input';
import { ControllerRenderProps } from 'react-hook-form';
import { useAppDispatch, useAppSelector } from 'shared/lib/store';
import { usePerson } from 'entities/person';
import { unmaskPhone } from 'shared/lib/string';
import { useDebounceCallback } from 'shared/lib/debounce';
import {
	selectDefaultPerson,
	selectPersonBookList,
} from 'entities/person/model/selector';
import {
	resetBookingPerson,
	restoreBookingData,
	selectBookingThreadMod,
	selectOrderDetail,
	updateBookingPersonId,
	updateBookingPersonName,
	updateBookingPersonNotes,
	updateBookingPersonPhone,
	updateBookingPersonTags,
	updateBookingTags,
  selectOrderDetailPerson
} from 'entities/orders';
import { MultiValue } from 'react-select';
import { Option } from 'shared/types';
import { PersonData } from 'entities/person';

import { PaginationHook, usePaginationFilterPersons } from 'shared/lib/pagination';
import { FormValues } from '../../types';
import { useClientForm } from './useClientForm';
import { OrderBookingData } from 'entities/orders/model/types';
import { localStorageItem } from 'shared/lib/core/lib/utils';
import { useBookingReset } from './useBookingReset';

export const useBookingClientForm = () => {
	const {
		register,
		control,
		setValue,
		reset,
		formState: { errors },
	} = useClientForm().formMethods;

	const { activeBookingBtn } = useClientForm();

	const registerWithMask = useHookFormMask(register);
	const dispatch = useAppDispatch();

	const { person, searchMode, setOpenPersonBook, setSearchMode, setPerson } = usePerson();

	const { fetchDataWithDispatch, filters } =
		usePaginationFilterPersons() as PaginationHook;
	const phoneMask = useRef('+7 (999) 999-99-99');
	const persons = useAppSelector(selectPersonBookList);
	const orderDetail = useAppSelector(selectOrderDetail);
	const orderDetailPerson = useAppSelector(selectOrderDetailPerson);
	const bookingThreadMod = useAppSelector(selectBookingThreadMod);
	const defaultPerson = useAppSelector(selectDefaultPerson);
	const savedData = localStorageItem('booking') as OrderBookingData;

	useEffect(() => {
		if (person) {
			const phone = person.phone || '';
			const name = person.name || '';
			const notes = person.notes || '';
			const personId = person.id;

			setValue('phone', phone);
			setValue('name', name);
			setValue('notes', notes);

			dispatch(updateBookingPersonName({ name }));
			dispatch(updateBookingPersonNotes({ notes }));
			dispatch(updateBookingPersonPhone({ phone }));

			if (personId) {
				dispatch(updateBookingPersonId({ id: personId }));
			}
		}
	}, [person, setValue, dispatch]);

	const debouncedDispatch = useDebounceCallback((search: string) => {
		fetchDataWithDispatch({ search, page: 1 });
	}, 500);

	const getPersonsByPhone = (event: ChangeEvent<HTMLInputElement>) => {
		const phone = event.target.value;
		const search = unmaskPhone(phone);
		setSearchMode('searchByPhone');
		dispatch(updateBookingPersonPhone({ phone: search }));
		if (search) {
			debouncedDispatch(search);
		}
		activeBookingBtn();
	};

	const searchAfterPaste = (phone: string) => {
		const search = unmaskPhone(phone);
		setSearchMode('searchByPhone');
		dispatch(updateBookingPersonPhone({ phone: search }));
		if (search) {
			debouncedDispatch(search);
		}
		activeBookingBtn();
	};

	useEffect(() => {
		if (searchMode === 'searchByPhone') {
			if (persons.length && filters.search?.length > 5) {
				setOpenPersonBook(true);
			} else if (filters.search?.length < 5) {
				setOpenPersonBook(false);
			} else if (filters.search?.length > 5 && !persons.length) {
				setOpenPersonBook(false);
			}
		}
	}, [persons, filters, searchMode, setOpenPersonBook]);

	const resetClientData = () => {
		reset();
		setPerson(null);
		dispatch(resetBookingPerson());
	};

	useBookingReset(() => {
		resetClientData();
		selectTags([]);
	});

	const changeName = (
		event: ChangeEvent<HTMLInputElement>,
		field: ControllerRenderProps<FormValues, 'name'>,
	) => {
		const name = event.target.value;
		field.onChange(event);
		dispatch(updateBookingPersonName({ name }));
		activeBookingBtn();
	};

	const changeNotes = (
		event: ChangeEvent<HTMLTextAreaElement>,
		field: ControllerRenderProps<FormValues, 'notes'>,
	) => {
		const notes = event.target.value;
		field.onChange(event);
		dispatch(updateBookingPersonNotes({ notes }));
		activeBookingBtn();
	};

	const selectTags = (values: MultiValue<Option>) => {
		const tags = values.map(val => val.label);

		dispatch(updateBookingPersonTags({ tags }));
		activeBookingBtn();
	};

	useEffect(() => {
		if (orderDetail) {
			const personId = orderDetail.person.id;
			const name = orderDetail.person.name || ' ';
			const phone = orderDetail.person.phone || ' ';
			const notes = orderDetail.person.notes || ' ';
			const tags = orderDetail.person.tags;

			const personData: PersonData = {
				id: personId,
				name,
				phone,
				notes,
				tags,
			};

			setPerson(personData);

			dispatch(updateBookingTags({ tags: orderDetail.tags }));

			return;
		}

		if (orderDetailPerson) {
			const personId = orderDetailPerson.id;
			const name = orderDetailPerson.name || ' ';
			const phone = orderDetailPerson.phone || ' ';
			const notes = orderDetailPerson.notes || ' ';
			const tags = orderDetailPerson.tags;

			const personData: PersonData = {
				id: personId,
				name,
				phone,
				notes,
				tags,
			};

			setPerson(personData);

			return;
		}

		if (savedData) {
			const personId = savedData.person.id;
			const name = savedData.person.name || '';
			const phone = savedData.person.phone || '';
			const notes = savedData.person.notes || '';
			const tags = savedData.person.tags;

			const personData: PersonData = {
				id: personId,
				name,
				phone,
				notes,
				tags,
			};

			setPerson(personData);

			dispatch(restoreBookingData(savedData));
			dispatch(updateBookingTags({ tags: savedData.tags }));
		}
		// eslint-disable-next-line
	}, [orderDetail, dispatch, setPerson]);

	useEffect(() => {
		if (bookingThreadMod && defaultPerson) {
			setPerson(defaultPerson);
		}
	}, [bookingThreadMod, defaultPerson, setPerson]);

	return {
		control,
		phoneMask,
		errors,
		person,
		searchAfterPaste,
		getPersonsByPhone,
		resetClientData,
		changeName,
		changeNotes,
		selectTags,
		registerWithMask,
		setPerson,
	};
};
